import moment from 'moment';
import cmsService, { extractGigcasterIdsFromChannel } from '../services/CMSService';

const normalizeFieldName = (value = '') => String(value).trim().toLowerCase();

const firstTruthy = (...values) => values.find(Boolean) || null;

const mergeDateAndTime = (dateValue, timeValue) => {
  if (!dateValue) return null;
  if (!timeValue) return dateValue;

  const dateText = String(dateValue).trim();
  const timeText = String(timeValue).trim();
  if (!dateText) return null;
  if (!timeText) return dateText;

  // If date already contains time, keep as-is.
  if (/T\d{1,2}:\d{2}| \d{1,2}:\d{2}/.test(dateText)) {
    return dateText;
  }

  return `${dateText} ${timeText}`;
};

export const getCustomFieldValue = (customFields, targetFieldName) => {
  if (!customFields || !targetFieldName) return null;

  if (!Array.isArray(customFields) && typeof customFields === 'object') {
    return customFields[targetFieldName] ?? null;
  }

  if (!Array.isArray(customFields)) return null;

  const normalizedTarget = normalizeFieldName(targetFieldName);
  const match = customFields.find(field => {
    const key = normalizeFieldName(field?.field_title || field?.name || field?.key);
    return key === normalizedTarget;
  });

  return match ? (match.field_value ?? match.value ?? null) : null;
};

// True when the value carries a full calendar date (not just a time-of-day), so
// it can stand in for the whole event date rather than being merged as a time.
const looksLikeFullDate = value =>
  !!value && /\d{4}-\d{2}-\d{2}|\d{1,2}\/\d{1,2}\/\d{2,4}/.test(String(value));

export const getChannelEventDateValue = (channel, slimEventData) => {
  // start_time is a full-datetime OVERRIDE of event_date (an ops-controlled
  // reschedule / manual start). When present as a full datetime it wins over
  // everything else — matching the live-status overlay's `start_time ?? event_date`
  // precedence so the same rule applies even outside the live-status fetch window.
  const startTimeOverride = [
    slimEventData?.start_time,
    channel?.start_time,
    getCustomFieldValue(channel?.custom_fields, 'start_time'),
  ].find(looksLikeFullDate);
  if (startTimeOverride) return startTimeOverride;

  const slimDateTime = firstTruthy(
    slimEventData?.starts_at,
    slimEventData?.startsAt,
    slimEventData?.date_time,
    slimEventData?.dateTime,
  );
  if (slimDateTime) return slimDateTime;

  const slimDate = firstTruthy(
    slimEventData?.event_date,
    slimEventData?.date,
    slimEventData?.eventDate,
    slimEventData?.start_date,
    slimEventData?.startDate,
  );
  const slimTime = firstTruthy(
    slimEventData?.event_time,
    slimEventData?.eventTime,
    slimEventData?.start_time,
    slimEventData?.startTime,
    slimEventData?.time,
  );
  const slimDateAndTime = mergeDateAndTime(slimDate, slimTime);
  if (slimDateAndTime) return slimDateAndTime;

  const channelDateTime = firstTruthy(
    channel?.starts_at,
    channel?.startsAt,
    channel?.date_time,
    channel?.dateTime,
  );
  if (channelDateTime) return channelDateTime;

  const channelDate = firstTruthy(
    channel?.event_date,
    channel?.date,
    channel?.eventDate,
    channel?.start_date,
    channel?.startDate,
  );
  const channelTime = firstTruthy(
    channel?.event_time,
    channel?.eventTime,
    channel?.start_time,
    channel?.startTime,
    channel?.time,
  );
  const channelDateAndTime = mergeDateAndTime(channelDate, channelTime);
  if (channelDateAndTime) return channelDateAndTime;

  const customFieldDate = firstTruthy(
    getCustomFieldValue(channel?.custom_fields, 'event_date'),
    getCustomFieldValue(channel?.custom_fields, 'start_date'),
    getCustomFieldValue(channel?.custom_fields, 'date'),
  );
  const customFieldTime = firstTruthy(
    getCustomFieldValue(channel?.custom_fields, 'event_time'),
    getCustomFieldValue(channel?.custom_fields, 'start_time'),
    getCustomFieldValue(channel?.custom_fields, 'time'),
  );

  return mergeDateAndTime(customFieldDate, customFieldTime);
};

export const formatChannelEventDate = rawDate => {
  if (!rawDate) return '';

  const raw = String(rawDate);
  const parsedUtc = moment.utc(raw);
  const parsedLocal = parsedUtc.isValid() ? parsedUtc.local() : moment(raw);
  if (!parsedLocal.isValid()) return '';

  const hasTime = /T\d{1,2}:\d{2}| \d{1,2}:\d{2}/.test(raw);
  const base = parsedLocal.format(hasTime ? 'MMM D, YYYY · h:mm A' : 'MMM D, YYYY');

  let tzAbbr = '';
  try {
    tzAbbr =
      new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
        .formatToParts(parsedLocal.toDate())
        .find(part => part.type === 'timeZoneName')?.value || '';
  } catch {
    // Ignore timezone abbreviation failures; date/time formatting still works.
  }

  return `${base}${tzAbbr ? ` ${tzAbbr}` : ''}`;
};

export const preloadSlimEventDataForChannels = async (channels = []) => {
  if (!channels.length) return 0;

  const idSet = new Set();
  const unresolvedChannels = [];

  channels.forEach(channel => {
    const ids = extractGigcasterIdsFromChannel(channel).filter(Boolean);
    if (ids.length > 0) {
      ids.forEach(id => idSet.add(id));
    } else {
      unresolvedChannels.push(channel);
    }
  });

  if (unresolvedChannels.length > 0) {
    const resolvedGroups = await Promise.all(
      unresolvedChannels.map(channel => cmsService.resolveGigcasterIdsForChannel(channel)),
    );
    resolvedGroups.forEach((ids = []) => {
      ids.filter(Boolean).forEach(id => idSet.add(id));
    });
  }

  const gigcasterIds = [...idSet];
  if (gigcasterIds.length === 0) return 0;

  await cmsService.preloadSlimEventData(gigcasterIds);
  return gigcasterIds.length;
};
